import * as THREE from 'three'

import Experience from '../Experience'
import Weapon, { WEAPON_CONFIGS, type WeaponKind } from './Weapon'
import Hud from './Hud'
import Scoreboard from './Scoreboard'
import ConnectionStatus from './ConnectionStatus'
import MobileControls, { isTouchDevice, toggleFullscreen } from './MobileControls'
import PlayerModel from './PlayerModel'
import Sound from './Sound'
import Impacts from './Impacts'
import Decals from './Decals'
import Blood from './Blood'
import Tracers from './Tracers'
import DamageNumbers from './DamageNumbers'
import GroundWeapons, { type GroundPickupInfo } from './GroundWeapons'
import { AkFireModel } from './recoil'
import * as M from './movement'
import NetClient from '../net/NetClient'
import RemotePlayers from '../net/RemotePlayers'
import { WEAPON_STATS, type NetEvent, type PlayerInput } from '@/net/protocol'

const SOUNDS = {
  fire1: '/cs/sound/ak47-1.wav',
  fire2: '/cs/sound/ak47-2.wav',
  clipout: '/cs/sound/ak47_clipout.wav',
  clipin: '/cs/sound/ak47_clipin.wav',
  bolt: '/cs/sound/ak47_boltpull.wav',
  m4a1_fire: '/cs/sound/m4a1-1.wav',
  m4a1_clipout: '/cs/sound/m4a1_clipout.wav',
  m4a1_clipin: '/cs/sound/m4a1_clipin.wav',
  m4a1_bolt: '/cs/sound/m4a1_boltpull.wav',
  mp5_fire1: '/cs/sound/mp5-1.wav',
  mp5_fire2: '/cs/sound/mp5-2.wav',
  mp5_clipout: '/cs/sound/mp5_clipout.wav',
  mp5_clipin: '/cs/sound/mp5_clipin.wav',
  mp5_bolt: '/cs/sound/mp5_slideback.wav',
  tmp_fire1: '/cs/sound/tmp-1.wav',
  tmp_fire2: '/cs/sound/tmp-2.wav',
  mac10_fire: '/cs/sound/mac10-1.wav',
  mac10_clipout: '/cs/sound/mac10_clipout.wav',
  mac10_clipin: '/cs/sound/mac10_clipin.wav',
  mac10_bolt: '/cs/sound/mac10_boltpull.wav',
  p90_fire: '/cs/sound/p90-1.wav',
  p90_clipout: '/cs/sound/p90_clipout.wav',
  p90_clipin: '/cs/sound/p90_clipin.wav',
  p90_bolt: '/cs/sound/p90_boltpull.wav',
  m3_fire: '/cs/sound/m3-1.wav',
  m3_insert: '/cs/sound/m3_insertshell.wav',
  m3_pump: '/cs/sound/m3_pump.wav',
  xm1014_fire: '/cs/sound/xm1014-1.wav',
  m249_fire1: '/cs/sound/m249-1.wav',
  m249_fire2: '/cs/sound/m249-2.wav',
  m249_boxout: '/cs/sound/m249_boxout.wav',
  m249_boxin: '/cs/sound/m249_boxin.wav',
  glock_fire1: '/cs/sound/glock18-1.wav',
  glock_fire2: '/cs/sound/glock18-2.wav',
  usp_fire1: '/cs/sound/usp1.wav',
  usp_fire2: '/cs/sound/usp2.wav',
  usp_clipout: '/cs/sound/usp_clipout.wav',
  usp_clipin: '/cs/sound/usp_clipin.wav',
  usp_bolt: '/cs/sound/usp_slideback.wav',
  deagle_fire1: '/cs/sound/deagle-1.wav',
  deagle_fire2: '/cs/sound/deagle-2.wav',
  deagle_clipout: '/cs/sound/de_clipout.wav',
  deagle_clipin: '/cs/sound/de_clipin.wav',
  p228_fire: '/cs/sound/p228-1.wav',
  p228_clipout: '/cs/sound/p228_clipout.wav',
  p228_clipin: '/cs/sound/p228_clipin.wav',
  p228_bolt: '/cs/sound/p228_slidepull.wav',
  fiveseven_fire: '/cs/sound/fiveseven-1.wav',
  fiveseven_clipout: '/cs/sound/fiveseven_clipout.wav',
  fiveseven_clipin: '/cs/sound/fiveseven_clipin.wav',
  fiveseven_bolt: '/cs/sound/fiveseven_slidepull.wav',
  elite_fire: '/cs/sound/elite_fire.wav',
  elite_clipout: '/cs/sound/elite_clipout.wav',
  elite_leftclipin: '/cs/sound/elite_leftclipin.wav',
  elite_bolt: '/cs/sound/elite_sliderelease.wav',
  knifeslash1: '/cs/sound/knife_slash1.wav',
  knifeslash2: '/cs/sound/knife_slash2.wav',
  knifedeploy: '/cs/sound/knife_deploy1.wav',
  knifehitwall: '/cs/sound/knife_hitwall1.wav',
  step1: '/cs/sound/pl_dirt1.wav',
  step2: '/cs/sound/pl_dirt2.wav',
  step3: '/cs/sound/pl_dirt3.wav',
  step4: '/cs/sound/pl_dirt4.wav',
  wade1: '/cs/sound/pl_wade1.wav',
  wade2: '/cs/sound/pl_wade2.wav',
  wade3: '/cs/sound/pl_wade3.wav',
  wade4: '/cs/sound/pl_wade4.wav',
  swim1: '/cs/sound/pl_swim1.wav',
  swim2: '/cs/sound/pl_swim2.wav',
  swim3: '/cs/sound/pl_swim3.wav',
  swim4: '/cs/sound/pl_swim4.wav',
}
const STEP_KEYS = ['step1', 'step2', 'step3', 'step4']
const WADE_KEYS = ['wade1', 'wade2', 'wade3', 'wade4']
const SWIM_KEYS = ['swim1', 'swim2', 'swim3', 'swim4']
const FIRE_KEYS: Record<WeaponKind, string[]> = {
  knife: [],
  ak: ['fire1', 'fire2'],
  m4a1: ['m4a1_fire'],
  mp5: ['mp5_fire1', 'mp5_fire2'],
  tmp: ['tmp_fire1', 'tmp_fire2'],
  p90: ['p90_fire'],
  mac10: ['mac10_fire'],
  m3: ['m3_fire'],
  xm1014: ['xm1014_fire'],
  m249: ['m249_fire1', 'm249_fire2'],
  glock: ['glock_fire1', 'glock_fire2'],
  usp: ['usp_fire1', 'usp_fire2'],
  deagle: ['deagle_fire1', 'deagle_fire2'],
  p228: ['p228_fire'],
  elite: ['elite_fire'],
  fiveseven: ['fiveseven_fire'],
}
const RELOAD_SOUNDS: Partial<
  Record<WeaponKind, { out: string; inside: string; bolt: string }>
> = {
  ak: { out: 'clipout', inside: 'clipin', bolt: 'bolt' },
  m4a1: { out: 'm4a1_clipout', inside: 'm4a1_clipin', bolt: 'm4a1_bolt' },
  mp5: { out: 'mp5_clipout', inside: 'mp5_clipin', bolt: 'mp5_bolt' },
  mac10: { out: 'mac10_clipout', inside: 'mac10_clipin', bolt: 'mac10_bolt' },
  p90: { out: 'p90_clipout', inside: 'p90_clipin', bolt: 'p90_bolt' },
  m3: { out: 'm3_insert', inside: 'm3_insert', bolt: 'm3_pump' },
  m249: { out: 'm249_boxout', inside: 'm249_boxin', bolt: 'bolt' },
  glock: { out: 'clipout', inside: 'clipin', bolt: 'bolt' },
  usp: { out: 'usp_clipout', inside: 'usp_clipin', bolt: 'usp_bolt' },
  deagle: { out: 'deagle_clipout', inside: 'deagle_clipin', bolt: 'bolt' },
  p228: { out: 'p228_clipout', inside: 'p228_clipin', bolt: 'p228_bolt' },
  elite: { out: 'elite_clipout', inside: 'elite_leftclipin', bolt: 'elite_bolt' },
  fiveseven: {
    out: 'fiveseven_clipout',
    inside: 'fiveseven_clipin',
    bolt: 'fiveseven_bolt',
  },
}

const DOWN = new THREE.Vector3(0, -1, 0)
const UP = new THREE.Vector3(0, 1, 0)

const SENSITIVITY = 0.003
const MOBILE_LOOK_SENS = 0.004 // radians per px of touch-drag
const MAX_PITCH = Math.PI / 2 - 0.05
const TP_MIN_PITCH = -0.45
const TP_MAX_PITCH = 0.7
// Death: replay the kill from the killer's angle, then a 3-2-1 respawn count.
// RESPAWN_TOTAL must match the server's RESPAWN_SECONDS.
const KILLCAM_TIME = 2.0
const RESPAWN_TOTAL = 5.0
const WALL_SKIN = 0.5
const WALL_CLEARANCE = M.RADIUS + WALL_SKIN
const WATER_MAX_SPEED = 150
const WATER_ACCELERATE = 5
const WATER_FRICTION = 2.2
const WATER_GRAVITY = 90
const WATER_UP_SPEED = 115
const WATER_DOWN_SPEED = 95
const WALL_DIRECTIONS = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
  new THREE.Vector3(1, 0, 1).normalize(),
  new THREE.Vector3(1, 0, -1).normalize(),
  new THREE.Vector3(-1, 0, 1).normalize(),
  new THREE.Vector3(-1, 0, -1).normalize(),
]
type ThirdPersonMode = 'follow' | 'inspect'
type PlayerAction = 'aim' | 'shoot' | 'reload' | 'death'
const PRIMARY_WEAPONS = new Set<WeaponKind>([
  'ak',
  'mp5',
  'tmp',
  'p90',
  'mac10',
  'm4a1',
  'm3',
  'xm1014',
  'm249',
])
const SECONDARY_WEAPONS = new Set<WeaponKind>([
  'glock',
  'usp',
  'deagle',
  'p228',
  'elite',
  'fiveseven',
])

/**
 * Counter-Strike 1.6-style player: mouse look (pointer-lock OR drag, so it works
 * on a trackpad too) + grounded movement with gravity, friction/acceleration,
 * crouch and jump, raycast collision against the map, an AK-47 view model and a
 * HUD. Keys match config.cfg.
 */
export default class PlayerController {
  private readonly experience = Experience.instance
  // CS slots: 1 = primary (empty until pickup), 2 = pistol, 3 = knife.
  private readonly weapons: Partial<Record<WeaponKind, Weapon>> = createStarterWeapons()
  private readonly ownedWeapons = new Set<WeaponKind>(['knife', 'glock'])
  private activeKind: WeaponKind = 'knife'
  private weapon: Weapon = this.getWeapon('knife')
  private lastPrimary?: WeaponKind
  private secondaryKind?: WeaponKind = 'glock'
  private readonly hud = new Hud()
  private readonly scoreboard = new Scoreboard()
  private readonly connectionStatus = new ConnectionStatus()
  private readonly mobile = isTouchDevice()
  private mobileControls?: MobileControls
  private readonly model = new PlayerModel()
  private readonly sound = new Sound(SOUNDS)
  private readonly impacts = new Impacts(this.experience.scene)
  private readonly decals = new Decals(this.experience.scene)
  private readonly blood = new Blood(this.experience.scene, (origin, dir, far) =>
    this.cast(origin, dir, far),
  )
  private readonly tracers = new Tracers(this.experience.scene)
  private readonly damageNumbers = new DamageNumbers()
  private readonly groundWeapons = new GroundWeapons(
    this.experience.scene,
    (position, dynamic) => this.snapPickupToFloor(position, dynamic),
  )
  private readonly net = new NetClient()
  private readonly remotePlayers = new RemotePlayers()
  private waterMeshSource?: THREE.Object3D
  private waterMeshes: THREE.Mesh[] = []
  private readonly underwaterFog = new THREE.Fog(0x55c7e8, 80, 1600)
  private normalFog?: THREE.Scene['fog']
  private underwaterVisual = false
  private stepTimer = 0
  private waterTimer = 0
  private inWater = false
  private shootAnimTimer = 0
  private shootAnimDuration = 0.12
  private inputSeq = 0
  private spawnSent = false
  // After a (re)spawn the server still reports our OLD position until it applies
  // the spawn we just sent. Suppress reconciliation until it agrees, otherwise
  // it yanks the fresh spawn back toward the death spot and we fall out of world.
  private posSettled = false
  private health = 100
  private armor = 100
  private dead = false
  private deathAt = 0
  private deathVariant = 0
  private readonly deathPoint = new THREE.Vector3()
  private readonly deathDir = new THREE.Vector3()
  private readonly countdown = {
    root: document.getElementById('death-countdown'),
    num: document.getElementById('death-countdown-num'),
  }
  private readonly killcamBanner = document.getElementById('death-killcam')
  private thirdPerson = false
  private thirdPersonMode: ThirdPersonMode = 'follow'
  private thirdPersonOrbitYaw = 0
  private thirdPersonOrbitPitch = 0.12

  private readonly feet = new THREE.Vector3()
  private readonly velocity = new THREE.Vector3()
  private readonly spawnFeet = new THREE.Vector3()
  private onGround = false
  private duckT = 0
  private initialized = false

  // Look state (we own yaw/pitch rather than three's PointerLockControls, so the
  // same camera works whether the pointer is locked or the user drags to look).
  private yaw = 0
  private pitch = 0
  private entered = false // user has clicked into the game
  private locked = false // pointer is actually locked (mouse: slide to look)
  private buttonDown = false // left button held (drag to look / fire)
  private button2Down = false // right button held (knife stab)
  private primaryLatched = false
  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ')
  private readonly aimQuat = new THREE.Quaternion()

  private readonly keys = new Set<string>()
  private clickTargets: (HTMLElement | null)[] = []

  private readonly ray = new THREE.Raycaster()
  // CS-accurate AK-47 recoil/spread (punchangle + accuracy), see recoil.ts.
  private readonly fire = new AkFireModel()
  private readonly netShotOrigin = new THREE.Vector3()
  private readonly netShotDir = new THREE.Vector3()
  private readonly netMuzzle = new THREE.Vector3()
  private readonly netRight = new THREE.Vector3()
  private readonly muzzleNdc = new THREE.Vector2()
  private readonly tracerRay = new THREE.Vector3()
  private readonly tracerStart = new THREE.Vector3()
  private pendingShot?:
    | {
        origin: THREE.Vector3
        dir: THREE.Vector3
        weapon: WeaponKind
        // Distance to the first wall along the shot, so the server can block
        // shots that would otherwise pass through geometry it can't see.
        maxDist: number
      }
    | undefined

  // scratch vectors (avoid per-frame allocation)
  private readonly fwd = new THREE.Vector3()
  private readonly right = new THREE.Vector3()
  private readonly wish = new THREE.Vector3()
  private readonly head = new THREE.Vector3()
  private readonly back = new THREE.Vector3()
  private readonly aimRight = new THREE.Vector3()
  private readonly aimUp = new THREE.Vector3()

  constructor() {
    const { canvas, renderer } = this.experience

    // Render the active weapon as an overlay on top of the world (1st person only).
    renderer.overlay = this.weaponOverlay()
    this.groundWeapons.onPickup = (info) => this.pickupWeapon(info)

    this.clickTargets = [canvas, document.getElementById('blocker')]
    for (const el of this.clickTargets)
      el?.addEventListener('click', this.onClick)
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
    // Mouse events (NOT pointer events): during pointer lock the browser fires
    // `mousemove` with movementX/Y, but may not fire `pointermove` — which is why
    // shooting worked while looking didn't.
    window.addEventListener('mousedown', this.onMouseDown)
    window.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('mousemove', this.onMouseMove)
    window.addEventListener('contextmenu', this.onContextMenu)
    document.addEventListener('pointerlockchange', this.onPointerLockChange)
    // Close the net connection when the tab is hidden/closed/reloaded so the
    // server doesn't accumulate zombie players (HMR reloads especially).
    window.addEventListener('pagehide', this.onPageHide)
    window.addEventListener('pageshow', this.onPageShow)
    // Belt-and-suspenders against the iOS long-press "Copy/Look Up" selection
    // callout and the pinch gesture during play.
    document.addEventListener('selectstart', this.onSelectStart)
    document.addEventListener('gesturestart', this.onSelectStart)

    // On phones/tablets, build the on-screen touch UI and adapt the entry hint.
    if (this.mobile) {
      this.mobileControls = new MobileControls({
        onScoreboard: () => this.scoreboard.toggle(),
        onThirdPerson: () => this.toggleThirdPersonBehind(),
      })
      const play = document.querySelector('.blocker .play')
      if (play) play.textContent = 'toca para jugar'
    }

    // Surface connection state and resync the local player after a reconnect
    // (clear posSettled so the server can re-adopt our position without a yank).
    this.net.onStatus = (status) => this.connectionStatus.set(status)
    // Wait for the server to re-adopt our position (RESYNC_STEP) before
    // reconciling again, so the reconnect doesn't yank us back (the old loop).
    this.net.onReconnect = () => {
      this.posSettled = false
      this.spawnSent = false
      window.setTimeout(() => this.sendSpawn(), 0)
    }
    this.net.connect()
  }

  private onPageHide = (): void => {
    this.net.disconnect()
  }

  private onPageShow = (): void => {
    if (!this.net.connected) {
      this.posSettled = false
      this.spawnSent = false
      this.net.connect()
    }
  }

  private onSelectStart = (e: Event): void => {
    e.preventDefault()
  }

  // ---- input ----------------------------------------------------------------

  private onClick = (): void => {
    if (!this.entered) this.enter()
    else if (!this.locked) this.requestLock()
  }

  private enter(): void {
    this.entered = true
    document.getElementById('blocker')?.classList.add('hidden')
    if (!this.dead) document.getElementById('crosshair')?.classList.remove('hidden')
    this.hud.setVisible(true)
    this.sound.resume()
    if (this.mobile) {
      // No pointer lock on touch; the on-screen controls drive input (pollMobile
      // treats the player as "locked" so firing/look work). Go fullscreen for an
      // immersive, zoom-proof view (best-effort; ignored on iOS).
      this.mobileControls?.setActive(true)
      this.locked = true
      if (!document.fullscreenElement) toggleFullscreen()
      return
    }
    if (!this.dead) this.requestLock()
  }

  /** Best-effort pointer lock. If it fails (common on trackpads), we fall back
   * to drag-to-look, so the game is still playable. */
  private requestLock(): void {
    const result = this.experience.canvas.requestPointerLock() as
      | Promise<void>
      | undefined
    if (result && typeof result.then === 'function') result.catch(() => {})
  }

  private onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.experience.canvas
  }

  private onMouseMove = (e: MouseEvent): void => {
    // Look when the pointer is locked (slide) OR while dragging (button held).
    // Robust even if pointer lock flaps (e.g. when DevTools steals focus).
    if (!this.locked && !this.buttonDown) return
    this.yaw -= e.movementX * SENSITIVITY
    this.pitch -= e.movementY * SENSITIVITY
    this.pitch = THREE.MathUtils.clamp(this.pitch, -MAX_PITCH, MAX_PITCH)
  }

  private onMouseDown = (e: MouseEvent): void => {
    if (this.dead) return
    if (e.button === 0) this.buttonDown = true
    if (e.button === 2) this.button2Down = true
  }

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) {
      this.buttonDown = false
      this.primaryLatched = false
    }
    if (e.button === 2) this.button2Down = false
  }

  // Suppress the browser context menu so right-click can be the knife stab.
  private onContextMenu = (e: MouseEvent): void => {
    if (this.entered) e.preventDefault()
  }

  private static readonly GAME_KEYS = new Set([
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Space',
  ])

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys.add(e.code)
    // Scoreboard: hold ` to see all players + ping (works while dead too).
    if (e.code === 'Backquote') {
      this.scoreboard.setVisible(true)
      e.preventDefault()
    }
    if (this.dead) return
    if (e.code === 'KeyR' && this.weapon.reload()) this.playReload()
    if (e.code === 'KeyG') this.dropActiveWeapon()
    if (e.code === 'KeyZ') this.toggleThirdPersonBehind()
    if (e.code === 'KeyX') this.showThirdPersonFront()
    if (e.code === 'Digit1') this.selectSlot('1')
    if (e.code === 'Digit2') this.selectSlot('2')
    if (e.code === 'Digit3') this.selectSlot('knife')
    if (PlayerController.GAME_KEYS.has(e.code)) e.preventDefault()
  }

  /** The active weapon's overlay scene/camera (first-person view model). */
  private weaponOverlay(): { scene: THREE.Scene; camera: THREE.Camera } {
    return { scene: this.weapon.scene, camera: this.weapon.camera }
  }

  /** Switch weapon slot: primary (1) or knife (3). Plays the draw + deploy SFX. */
  public selectSlot(key: WeaponKind | '1' | '2' | '3'): void {
    const kind: WeaponKind =
      key === '1'
        ? (this.lastPrimary ?? 'knife')
        : key === '2'
          ? (this.secondaryKind ?? 'knife')
          : key === '3'
            ? 'knife'
            : key
    if (!this.ownedWeapons.has(kind)) return
    if (kind === this.activeKind) return
    this.activeKind = kind
    this.weapon = this.getWeapon(kind)
    this.weapon.equip()
    this.fire.reset() // CS resets accuracy + shot count on deploy
    this.model.setWeapon(kind)
    if (!this.thirdPerson) {
      this.experience.renderer.overlay = this.weaponOverlay()
    }
    this.sound.play(kind === 'knife' ? 'knifedeploy' : 'bolt', 0.5)
  }

  private pickupWeapon(info: GroundPickupInfo): boolean {
    const kind = info.kind
    if (kind === 'knife' || this.ownedWeapons.has(kind)) return false
    if (PRIMARY_WEAPONS.has(kind) && this.lastPrimary) return false
    if (SECONDARY_WEAPONS.has(kind) && this.secondaryKind) return false
    const weapon = this.getWeapon(kind)
    // A map spawn grants a full loadout; a dropped weapon keeps the rounds it
    // carried (info.ammo/reserve).
    const ammo = info.source === 'drop' && info.ammo !== undefined ? info.ammo : weapon.clipSize
    const reserve =
      info.source === 'drop' && info.reserve !== undefined
        ? info.reserve
        : WEAPON_CONFIGS[kind].reserve
    weapon.ammo = ammo
    weapon.reserve = reserve
    this.ownedWeapons.add(kind)
    if (SECONDARY_WEAPONS.has(kind)) this.secondaryKind = kind
    else this.lastPrimary = kind
    this.selectSlot(kind)
    // Tell the authoritative server the rounds this floor item carried, or it
    // would grant a full loadout on the weapon switch (overriding a dropped clip).
    this.net.sendWeaponPickup({ weapon: kind, ammo, reserve })
    return true
  }

  private getWeapon(kind: WeaponKind): Weapon {
    let weapon = this.weapons[kind]
    if (!weapon) {
      weapon = new Weapon(WEAPON_CONFIGS[kind])
      const { width, height } = this.experience.sizes
      weapon.resize(width / height)
      this.weapons[kind] = weapon
    }
    return weapon
  }

  private dropActiveWeapon(): void {
    if (this.activeKind === 'knife') return
    const dropped = this.activeKind
    const ammo = this.weapon.ammo
    const reserve = this.weapon.reserve
    this.updateAimBasis()
    const pos = this.aimOrigin(new THREE.Vector3()).addScaledVector(this.fwd, 38)
    pos.y -= 16
    const vel = this.fwd.clone().multiplyScalar(180)
    vel.y += 90
    // An empty weapon (no rounds left) isn't left on the floor — it just
    // vanishes. Anything with bullets becomes a real, ammo-carrying pickup.
    if (ammo + reserve > 0) {
      this.groundWeapons.addDropped(dropped, pos, this.yaw, {
        velocity: vel,
        ammo,
        reserve,
      })
      this.net.sendWeaponDrop({
        weapon: dropped,
        pos: { x: pos.x, y: pos.y, z: pos.z },
        vel: { x: vel.x, y: vel.y, z: vel.z },
        yaw: this.yaw,
        ammo,
        reserve,
      })
    }
    this.ownedWeapons.delete(dropped)
    if (this.lastPrimary === dropped) this.lastPrimary = this.findOwned(PRIMARY_WEAPONS)
    if (this.secondaryKind === dropped) {
      this.secondaryKind = this.findOwned(SECONDARY_WEAPONS)
    }
    this.selectSlot(this.secondaryKind ?? this.lastPrimary ?? 'knife')
  }

  private findOwned(kinds: Set<WeaponKind>): WeaponKind | undefined {
    for (const kind of this.ownedWeapons) {
      if (kinds.has(kind)) return kind
    }
    return undefined
  }

  /** Reload SFX: mag/box/shell out, in, then bolt/pump — roughly timed. */
  private playReload(): void {
    this.fire.reset() // CS resets accuracy + shot count on reload
    const sounds = RELOAD_SOUNDS[this.activeKind] ?? RELOAD_SOUNDS.ak
    if (!sounds) return
    this.sound.play(sounds.out, 0.6)
    window.setTimeout(() => this.sound.play(sounds.inside, 0.6), 1100)
    window.setTimeout(() => this.sound.play(sounds.bolt, 0.6), 1900)
  }

  private setThirdPerson(visible: boolean): void {
    this.thirdPerson = visible
    this.model.setVisible(this.thirdPerson)
    this.experience.renderer.overlay = this.thirdPerson
      ? undefined
      : this.weaponOverlay()
  }

  /** Toggle first/third person behind the player. */
  private toggleThirdPersonBehind(): void {
    if (this.thirdPerson && this.thirdPersonMode === 'follow') {
      this.setThirdPerson(false)
      return
    }
    this.thirdPersonMode = 'follow'
    this.thirdPersonOrbitYaw = 0
    this.thirdPersonOrbitPitch = 0.12
    this.setThirdPerson(true)
  }

  public toggleView(): void {
    this.toggleThirdPersonBehind()
  }

  /** Show third person from the front for inspecting the player model. */
  private showThirdPersonFront(): void {
    this.thirdPersonMode = 'inspect'
    this.thirdPersonOrbitYaw = Math.PI
    this.thirdPersonOrbitPitch = 0.12
    this.setThirdPerson(true)
  }
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code)
    if (e.code === 'Backquote') this.scoreboard.setVisible(false)
  }

  // ---- collision helpers ----------------------------------------------------

  private get mapGroup(): THREE.Object3D | undefined {
    return this.experience.world.map.built?.object
  }

  private cast(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
    far: number,
  ): THREE.Intersection | null {
    const group = this.mapGroup
    if (!group) return null
    this.ray.set(origin, dir)
    this.ray.far = far
    return (
      this.ray
        .intersectObject(group, true)
        .find((hit) => PlayerController.isSolidObject(hit.object)) ?? null
    )
  }

  private static isSolidObject(object: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current.userData.nonSolid) return false
      current = current.parent
    }
    return true
  }

  private snapPickupToFloor(position: THREE.Vector3, dynamic = false): THREE.Vector3 {
    const group = this.mapGroup
    if (!group) return position.clone()
    this.ray.set(
      new THREE.Vector3(position.x, position.y + (dynamic ? 24 : 96), position.z),
      DOWN,
    )
    this.ray.far = dynamic ? 80 : 768
    const hits = this.ray
      .intersectObject(group, true)
      .filter((hit) => {
        if (!PlayerController.isSolidObject(hit.object)) return false
        if (hit.point.y > position.y + 8) return false
        const ny = Math.abs(hit.face?.normal.y ?? 1)
        return ny > 0.45
      })
    if (hits.length === 0) return position.clone()

    const floor = hits.reduce((best, hit) =>
      hit.point.y > best.point.y ? hit : best,
    )
    return new THREE.Vector3(position.x, floor.point.y, position.z)
  }

  private currentWater():
    | { surfaceY: number; depth: number; eyeUnder: boolean }
    | null {
    const areas = this.experience.world.map.built?.waterAreas
    if (!areas || areas.length === 0) return null
    for (const area of areas) {
      if (this.feet.y > area.surfaceY + 2) continue
      if (!pointInPolygonXZ(this.feet.x, this.feet.z, area.points)) continue
      const depth = area.surfaceY - this.feet.y
      if (depth <= 0) continue
      return {
        surfaceY: area.surfaceY,
        depth,
        eyeUnder: this.feet.y + this.eyeHeight() < area.surfaceY,
      }
    }
    return null
  }

  private updateWaterVisuals(eyeUnder: boolean): void {
    this.collectWaterMeshes()
    if (eyeUnder === this.underwaterVisual) return

    this.underwaterVisual = eyeUnder
    for (const mesh of this.waterMeshes) mesh.visible = !eyeUnder

    const scene = this.experience.scene
    if (eyeUnder) {
      this.normalFog = scene.fog instanceof THREE.Fog ? scene.fog.clone() : scene.fog
      scene.fog = this.underwaterFog
    } else if (this.normalFog !== undefined) {
      scene.fog = this.normalFog
    }
  }

  private collectWaterMeshes(): void {
    const group = this.mapGroup
    if (!group || group === this.waterMeshSource) return

    this.waterMeshSource = group
    this.waterMeshes = []
    group.traverse((child) => {
      if (child instanceof THREE.Mesh && child.userData.waterSurface) {
        this.waterMeshes.push(child)
      }
    })
  }

  private hitNormal(
    hit: THREE.Intersection,
    rayDir: THREE.Vector3,
  ): THREE.Vector3 {
    const n = hit.face
      ? hit.face.normal.clone().transformDirection(hit.object.matrixWorld)
      : rayDir.clone().negate()
    // BSP faces can be hit from either side. Keep effects on the visible side
    // by making the surface normal point back toward the shot origin.
    if (n.dot(rayDir) > 0) n.negate()
    return n.normalize()
  }

  /** Place the player at a map spawn (info_player_*), respecting the editor's
   * position and facing. Drops to the floor starting at the entity origin like
   * the engine, with a short range so it never lands on a roof/structure above
   * (the cause of the "spawn on the ceiling" bug) nor a far basement below. */
  private placeAtSpawn(spawn: { position: THREE.Vector3; angle: number }): void {
    this.feet.copy(spawn.position) // spawn.position is the feet (mapper-aligned)
    const hit = this.cast(
      new THREE.Vector3(this.feet.x, this.feet.y + 36, this.feet.z),
      DOWN,
      96,
    )
    if (hit) this.feet.y = hit.point.y
    this.velocity.set(0, 0, 0)
    this.duckT = 0
    // GoldSrc `angle` (deg, 0 = +X) → our look yaw. Respect the configured facing.
    this.yaw = THREE.MathUtils.degToRad(spawn.angle - 90)
    this.pitch = 0
  }

  /** A random map spawn (info_player_*), or undefined if the map has none. */
  private randomSpawn(): { position: THREE.Vector3; angle: number } | undefined {
    const spawns = this.experience.world.map.built?.spawns
    if (!spawns || spawns.length === 0) return undefined
    return spawns[(Math.random() * spawns.length) | 0]
  }

  private init(): void {
    const spawn = this.randomSpawn()
    if (!spawn) return
    this.placeAtSpawn(spawn)
    this.spawnFeet.copy(this.feet)
    this.initialized = true
    this.sendSpawn()
  }

  private sendSpawn(): void {
    if (this.spawnSent || !this.net.connected) return
    this.net.sendSpawn({
      pos: { x: this.feet.x, y: this.feet.y, z: this.feet.z },
      yaw: this.yaw,
      pitch: this.pitch,
    })
    this.spawnSent = true
    // We just teleported; hold off reconciliation until the server catches up.
    this.posSettled = false
  }

  private moveToRespawnPoint(): void {
    const spawn = this.randomSpawn()
    if (!spawn) {
      this.feet.copy(this.spawnFeet)
      return
    }
    this.placeAtSpawn(spawn)
  }

  /** Arrow keys rotate the camera (handy when the mouse/trackpad is fiddly). */
  private applyKeyLook(dt: number): void {
    const turn = 2.0 * dt // radians/second
    if (this.thirdPerson && this.thirdPersonMode === 'inspect') {
      if (this.keys.has('ArrowLeft')) this.thirdPersonOrbitYaw += turn
      if (this.keys.has('ArrowRight')) this.thirdPersonOrbitYaw -= turn
      if (this.keys.has('ArrowUp')) this.thirdPersonOrbitPitch += turn
      if (this.keys.has('ArrowDown')) this.thirdPersonOrbitPitch -= turn
      this.thirdPersonOrbitPitch = THREE.MathUtils.clamp(
        this.thirdPersonOrbitPitch,
        TP_MIN_PITCH,
        TP_MAX_PITCH,
      )
      return
    }
    if (this.keys.has('ArrowLeft')) this.yaw += turn
    if (this.keys.has('ArrowRight')) this.yaw -= turn
    if (this.keys.has('ArrowUp')) this.pitch += turn
    if (this.keys.has('ArrowDown')) this.pitch -= turn
    this.pitch = THREE.MathUtils.clamp(this.pitch, -MAX_PITCH, MAX_PITCH)
  }

  /** Apply the on-screen touch controls to the same state the keyboard/mouse use:
   * look from the drag, fire/jump/duck via virtual buttons, reload/switch edges. */
  private pollMobile(): void {
    const mc = this.mobileControls
    if (!mc) return
    this.locked = true // touch UI is the input source; treat as "locked"
    const { dx, dy } = mc.consumeLook()
    this.yaw -= dx * MOBILE_LOOK_SENS
    this.pitch = THREE.MathUtils.clamp(
      this.pitch - dy * MOBILE_LOOK_SENS,
      -MAX_PITCH,
      MAX_PITCH,
    )
    this.buttonDown = mc.fire
    if (mc.jump) this.keys.add('Space')
    else this.keys.delete('Space')
    if (mc.crouch) this.keys.add('ControlLeft')
    else this.keys.delete('ControlLeft')
    if (mc.consumeReload() && this.weapon.reload()) this.playReload()
    if (mc.consumeSwitch()) {
      this.selectSlot(this.activeKind === 'knife' ? '2' : 'knife')
    }
  }

  /** Movement axes from whichever input is active: analog stick on touch, or
   * WASD on desktop. forward +1 = ahead, right +1 = strafe right. */
  private inputAxes(): { forward: number; right: number } {
    if (this.mobileControls && this.entered) {
      return { forward: this.mobileControls.move.y, right: this.mobileControls.move.x }
    }
    return {
      forward: (this.keys.has('KeyW') ? 1 : 0) - (this.keys.has('KeyS') ? 1 : 0),
      right: (this.keys.has('KeyD') ? 1 : 0) - (this.keys.has('KeyA') ? 1 : 0),
    }
  }

  /** Footstep SFX while moving on the ground (faster cadence when running). */
  private updateFootsteps(dt: number, speed01: number): void {
    if (this.inWater) {
      this.stepTimer = 0.15
      this.updateWaterMovementSound(dt)
      return
    }
    if (!this.onGround || speed01 < 0.12 || this.duckT > 0.5) {
      this.stepTimer = 0.15 // small delay before the next step after stopping
      return
    }
    this.stepTimer -= dt
    if (this.stepTimer <= 0) {
      this.sound.playOneOf(STEP_KEYS, 0.3, 0.95 + Math.random() * 0.1)
      this.stepTimer = speed01 > 0.55 ? 0.3 : 0.45
    }
  }

  private updateWaterState(
    water: { surfaceY: number; depth: number; eyeUnder: boolean } | null,
  ): void {
    const nowInWater = water !== null && water.depth > 4
    if (nowInWater && !this.inWater) {
      this.sound.playOneOf(WADE_KEYS, 0.45, 0.95 + Math.random() * 0.08)
      this.waterTimer = 0.2
    } else if (!nowInWater && this.inWater) {
      this.sound.playOneOf(WADE_KEYS, 0.35, 1.0 + Math.random() * 0.08)
      this.waterTimer = 0.25
    }
    this.inWater = nowInWater
  }

  private updateWaterMovementSound(dt: number): void {
    const speed = this.velocity.length()
    if (speed < 35) {
      this.waterTimer = Math.min(this.waterTimer, 0.2)
      return
    }
    this.waterTimer -= dt
    if (this.waterTimer <= 0) {
      const deep = this.currentWater()?.eyeUnder ?? false
      this.sound.playOneOf(
        deep ? SWIM_KEYS : WADE_KEYS,
        deep ? 0.34 : 0.28,
        0.95 + Math.random() * 0.12,
      )
      this.waterTimer = deep ? 0.42 : 0.34
    }
  }

  /** Open the crosshair with movement, jumping and firing (CS-style spread). */
  private updateCrosshair(speed01: number): void {
    const el = document.getElementById('crosshair')
    if (!el) return
    let gap = 4 + speed01 * 16
    if (!this.onGround) gap += 14
    if (this.shootAnimTimer > 0) gap += 10
    el.style.setProperty('--gap', `${gap.toFixed(1)}px`)
  }

  private beginLocalDeath(
    point: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
  ): void {
    if (this.dead) return
    this.dead = true
    this.deathAt = this.experience.time.elapsed / 1000
    this.deathVariant = Math.floor(Math.random() * 8)
    this.deathPoint.set(point.x, point.y, point.z)
    this.deathDir.set(dir.x, dir.y, dir.z)
    if (this.deathDir.lengthSq() > 1e-6) this.deathDir.normalize()
    this.health = 0
    this.velocity.set(0, 0, 0)
    this.buttonDown = false
    this.button2Down = false
    this.keys.clear()
    this.model.setVisible(true)
    this.experience.renderer.overlay = undefined
    document.getElementById('crosshair')?.classList.add('hidden')
    // The kill-cam (replay) shows first; the countdown appears after it.
    this.killcamBanner?.classList.remove('hidden')
    this.countdown.root?.classList.add('hidden')
  }

  private endLocalDeath(): void {
    this.dead = false
    this.health = 100
    this.armor = 100
    this.countdown.root?.classList.add('hidden')
    this.killcamBanner?.classList.add('hidden')
    if (this.entered) document.getElementById('crosshair')?.classList.remove('hidden')
    this.setThirdPerson(false)
    this.experience.renderer.overlay = this.weaponOverlay()
  }

  private updateDeathCountdown(): void {
    if (!this.dead) return
    const elapsed = this.experience.time.elapsed / 1000 - this.deathAt
    const remaining = Math.max(1, Math.min(3, Math.ceil(RESPAWN_TOTAL - elapsed)))
    if (this.countdown.num) this.countdown.num.textContent = String(remaining)
  }

  private updateDeathCamera(): void {
    const cam = this.experience.camera.instance
    const elapsed = this.experience.time.elapsed / 1000 - this.deathAt

    // Keep the look target tracking the body as it settles onto the floor.
    const settle = Math.min(1, elapsed / 0.9)
    const lookY = THREE.MathUtils.lerp(40, 18, settle)
    this.head.set(this.feet.x, this.feet.y + lookY, this.feet.z)

    // Phase 1 — kill-cam: look at the corpse from the killer's side (back along
    // the shot), pushing in and drifting for a cinematic replay of the death.
    if (elapsed < KILLCAM_TIME && this.deathDir.lengthSq() > 1e-6) {
      this.killcamBanner?.classList.remove('hidden')
      this.countdown.root?.classList.add('hidden')
      const k = elapsed / KILLCAM_TIME
      this.back.copy(this.deathDir).multiplyScalar(-1) // toward the shooter
      this.back.y = 0
      if (this.back.lengthSq() < 1e-6) this.back.set(Math.sin(this.yaw), 0, Math.cos(this.yaw))
      this.back.normalize()
      const drift = (k - 0.5) * 0.6
      const c = Math.cos(drift)
      const s = Math.sin(drift)
      this.back.set(
        this.back.x * c - this.back.z * s,
        0,
        this.back.x * s + this.back.z * c,
      )
      // Close, framed shot so the collapse is clearly visible (a far camera made
      // the corpse tiny). Push in slightly and clamp off walls behind the camera.
      const dist = THREE.MathUtils.lerp(135, 95, k)
      const hit = this.cast(this.head, this.back, dist + 16)
      const useDist = hit ? Math.min(dist, Math.max(48, hit.distance - 12)) : dist
      cam.position.set(
        this.head.x + this.back.x * useDist,
        this.feet.y + THREE.MathUtils.lerp(56, 40, k),
        this.head.z + this.back.z * useDist,
      )
      cam.lookAt(this.head)
      return
    }

    // Phase 2 — respawn countdown: slow orbit around the corpse.
    this.killcamBanner?.classList.add('hidden')
    this.countdown.root?.classList.remove('hidden')
    this.updateDeathCountdown()
    const angle = this.yaw + Math.PI + Math.min(elapsed, RESPAWN_TOTAL) * 0.14
    const dist = 120
    const hit = this.cast(this.head, this.back.set(Math.sin(angle), 0, Math.cos(angle)), dist + 16)
    const useDist = hit ? Math.max(40, hit.distance - 12) : dist
    cam.position.set(
      this.head.x + Math.sin(angle) * useDist,
      this.feet.y + 56,
      this.head.z + Math.cos(angle) * useDist,
    )
    cam.lookAt(this.head)
  }

  /** Apply yaw/pitch (+ recoil punchangle) to the camera orientation. The punch
   * rotates the actual view, so bullets fired along it climb with the recoil —
   * exactly as CS aims along `v_angle + punchangle`. */
  private applyLook(): void {
    this.updateAimBasis()
    this.experience.camera.instance.quaternion.copy(this.aimQuat)
  }

  private eyeHeight(): number {
    return THREE.MathUtils.lerp(M.STAND_EYE, M.DUCK_EYE, this.duckT)
  }

  private aimOrigin(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.feet.x, this.feet.y + this.eyeHeight(), this.feet.z)
  }

  private updateAimBasis(): void {
    const punchPitch = THREE.MathUtils.degToRad(this.fire.pitch)
    const punchYaw = THREE.MathUtils.degToRad(this.fire.yaw)
    this.euler.set(this.pitch + punchPitch, this.yaw + punchYaw, 0)
    this.aimQuat.setFromEuler(this.euler)
    this.fwd.set(0, 0, -1).applyQuaternion(this.aimQuat).normalize()
    this.aimRight.set(1, 0, 0).applyQuaternion(this.aimQuat).normalize()
    this.aimUp.set(0, 1, 0).applyQuaternion(this.aimQuat).normalize()
  }

  // ---- per-frame ------------------------------------------------------------

  public update(dt: number): void {
    // Clamp dt so a stutter can't tunnel the player through the floor.
    const step = Math.min(dt, 0.05)

    if (!this.initialized) {
      if (this.mapGroup) this.init()
      else return
    }
    const builtMap = this.experience.world.map.built
    if (builtMap) this.groundWeapons.setPickups(builtMap.armoury)
    if (!this.dead) this.sendSpawn()

    // Feed touch input (look/fire/jump/duck/reload/switch) before look + movement.
    if (this.mobile && this.entered && !this.dead) this.pollMobile()

    if (!this.dead && this.entered) this.applyKeyLook(step)
    if (!this.dead) this.applyLook()

    const speed01 = Math.min(
      Math.hypot(this.velocity.x, this.velocity.z) / M.MAX_SPEED,
      1,
    )
    this.weapon.update(step, !this.dead && this.entered ? speed01 : 0)

    if (!this.dead && this.entered) {
      this.handleDuck(step)
      this.move(step)
      this.handleFiring()
      this.groundWeapons.update(this.feet, step)
      this.sendNetInput(step)
      this.updateFootsteps(step, speed01)
      this.updateCrosshair(speed01)
    }
    // Process net events first (a death event carries the precise kill dir for
    // the kill-cam), then let applyServerState fall back to the authoritative
    // alive flag if those one-shot events were dropped.
    this.updateRemotePlayers()
    this.applyServerState()

    // Decay the recoil punch + shot counter (firing only while holding the gun).
    const firingGun = !this.weapon.melee && this.locked && this.buttonDown
    this.fire.update(step, this.experience.time.elapsed / 1000, firingGun)
    this.impacts.update(step)
    this.blood.update(step)
    this.tracers.update(step)
    this.damageNumbers.update(step)
    this.updateWaterVisuals(this.currentWater()?.eyeUnder ?? false)

    const eye = this.eyeHeight()
    const cam = this.experience.camera.instance

    // Upper-body action: reload > shoot > aim.
    this.shootAnimTimer = Math.max(0, this.shootAnimTimer - step)
    let action: PlayerAction = 'aim'
    let actionT = 0
    if (this.dead) {
      action = 'death'
      actionT =
        this.deathVariant +
        Math.min(0.98, (this.experience.time.elapsed / 1000 - this.deathAt) / 0.9)
    } else if (this.weapon.reloading) {
      action = 'reload'
      actionT = this.weapon.reloadProgress
    } else if (this.shootAnimTimer > 0) {
      action = 'shoot'
      actionT =
        1 -
        this.shootAnimTimer / Math.max(this.shootAnimDuration, Number.EPSILON)
    }

    // Pose the third-person body (cheap; only drawn when visible).
    this.model.setPose(
      this.feet,
      this.yaw,
      this.pitch,
      this.duckT,
      this.dead ? 0 : speed01,
      this.experience.time.elapsed / 1000,
      action,
      actionT,
    )

    if (this.dead) {
      this.updateDeathCamera()
    } else if (this.thirdPerson) {
      this.head.set(this.feet.x, this.feet.y + eye, this.feet.z)
      if (this.thirdPersonMode === 'follow') {
        this.updateAimBasis()
        this.back.copy(this.fwd).negate()
      } else {
        const orbitYaw = this.yaw + this.thirdPersonOrbitYaw
        const cosPitch = Math.cos(this.thirdPersonOrbitPitch)
        this.back.set(
          Math.sin(orbitYaw) * cosPitch,
          Math.sin(this.thirdPersonOrbitPitch),
          Math.cos(orbitYaw) * cosPitch,
        )
      }
      const hit = this.cast(this.head, this.back, M.TP_DIST + 16)
      const dist = hit ? Math.max(30, hit.distance - 12) : M.TP_DIST
      cam.position.set(
        this.head.x + this.back.x * dist,
        this.head.y + this.back.y * dist,
        this.head.z + this.back.z * dist,
      )
      if (this.thirdPersonMode === 'follow') {
        cam.quaternion.copy(this.aimQuat)
      } else {
        cam.lookAt(this.head)
      }
    } else {
      cam.position.set(this.feet.x, this.feet.y + eye, this.feet.z)
    }

    this.hud.update({
      health: this.health,
      armor: this.armor,
      ammo: this.weapon.ammo,
      reserve: this.weapon.reserve,
      weapon: this.weapon.name,
      team: 'TERRORIST',
      melee: this.weapon.melee,
    })
    this.scoreboard.update(this.net.latestPlayers, this.net.id)

    // Fell out of the world → respawn.
    if (this.feet.y < -8000) {
      this.feet.copy(this.spawnFeet)
      this.velocity.set(0, 0, 0)
    }
  }

  private handleDuck(dt: number): void {
    const wantDuck =
      this.keys.has('ControlLeft') || this.keys.has('ControlRight')
    const dir = wantDuck || !this.canStand() ? 1 : -1
    this.duckT = THREE.MathUtils.clamp(
      this.duckT + (dir * dt) / M.DUCK_TIME,
      0,
      1,
    )
  }

  private canStand(): boolean {
    if (this.duckT <= 0) return true
    const origin = new THREE.Vector3(
      this.feet.x,
      this.feet.y + M.DUCK_HEIGHT,
      this.feet.z,
    )
    return !this.cast(origin, UP, M.STAND_HEIGHT - M.DUCK_HEIGHT + 1)
  }

  private move(dt: number): void {
    const water = this.currentWater()
    this.updateWaterState(water)
    if (water && water.depth > 6) {
      this.moveWater(dt, water)
      return
    }

    const cam = this.experience.camera.instance

    // Horizontal forward/right from the camera yaw.
    cam.getWorldDirection(this.fwd)
    this.fwd.y = 0
    if (this.fwd.lengthSq() < 1e-6) this.fwd.set(0, 0, -1)
    this.fwd.normalize()
    this.right.set(-this.fwd.z, 0, this.fwd.x)

    // WASD (desktop) or the analog touch stick; arrow keys are for looking.
    const ax = this.inputAxes()

    this.wish
      .set(0, 0, 0)
      .addScaledVector(this.fwd, ax.forward)
      .addScaledVector(this.right, ax.right)
    const hasWish = this.wish.lengthSq() > 1e-6
    if (hasWish) this.wish.normalize()

    const ducking = this.duckT > 0.5
    const walking = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight')
    const factor = ducking ? M.DUCK_FACTOR : walking ? M.WALK_FACTOR : 1
    const wishSpeed = hasWish ? M.MAX_SPEED * factor : 0

    this.applyFriction(dt)
    this.accelerate(this.wish, wishSpeed, dt)

    // Gravity + jump.
    this.velocity.y -= M.GRAVITY * dt
    if (this.onGround && this.keys.has('Space')) {
      this.velocity.y = M.JUMP_SPEED
      this.onGround = false
    }

    this.moveHorizontal(dt)
    this.moveVertical(dt)
    this.resolveHorizontalPenetration()
  }

  private moveWater(
    dt: number,
    water: { surfaceY: number; depth: number; eyeUnder: boolean },
  ): void {
    const cam = this.experience.camera.instance
    cam.getWorldDirection(this.fwd)
    this.fwd.normalize()
    this.right.set(-this.fwd.z, 0, this.fwd.x)
    if (this.right.lengthSq() < 1e-6) this.right.set(1, 0, 0)
    this.right.normalize()

    const ax = this.inputAxes()
    this.wish
      .set(0, 0, 0)
      .addScaledVector(this.fwd, ax.forward)
      .addScaledVector(this.right, ax.right)

    if (this.keys.has('Space')) this.wish.y += 1
    if (this.keys.has('ControlLeft') || this.keys.has('ControlRight'))
      this.wish.y -= 1

    const hasWish = this.wish.lengthSq() > 1e-6
    if (hasWish) this.wish.normalize()

    const speed = this.velocity.length()
    if (speed > 0.1) {
      const drop = speed * WATER_FRICTION * dt
      this.velocity.multiplyScalar(Math.max(0, speed - drop) / speed)
    }

    if (hasWish) this.accelerateWater(this.wish, WATER_MAX_SPEED, dt)

    if (!this.keys.has('Space')) {
      this.velocity.y -= WATER_GRAVITY * dt
      if (!water.eyeUnder && water.depth < 24) {
        this.velocity.y -= WATER_GRAVITY * dt
      }
    } else {
      this.velocity.y = Math.max(this.velocity.y, WATER_UP_SPEED)
    }
    if (this.keys.has('ControlLeft') || this.keys.has('ControlRight')) {
      this.velocity.y = Math.min(this.velocity.y, -WATER_DOWN_SPEED)
    }

    // Soft clamp at the surface: holding jump lets the player bob at waterline.
    if (this.feet.y > water.surfaceY - 3 && this.velocity.y > 0) {
      this.velocity.y *= 0.35
    }

    this.onGround = false
    this.moveHorizontal(dt)
    this.moveVertical(dt)
    this.resolveHorizontalPenetration()
  }

  private accelerateWater(
    dir: THREE.Vector3,
    wishSpeed: number,
    dt: number,
  ): void {
    const current = this.velocity.dot(dir)
    const add = wishSpeed - current
    if (add <= 0) return
    const accelSpeed = Math.min(WATER_ACCELERATE * wishSpeed * dt, add)
    this.velocity.addScaledVector(dir, accelSpeed)
  }

  private movementAxes(): { forward: number; right: number } {
    return this.inputAxes()
  }

  private sendNetInput(dt: number): void {
    const axes = this.movementAxes()
    const input: PlayerInput = {
      seq: ++this.inputSeq,
      clientTime: this.net.estimatedServerTime,
      dt,
      pos: { x: this.feet.x, y: this.feet.y, z: this.feet.z },
      vel: { x: this.velocity.x, y: this.velocity.y, z: this.velocity.z },
      forward: axes.forward,
      right: axes.right,
      jump: this.keys.has('Space'),
      duck: this.keys.has('ControlLeft') || this.keys.has('ControlRight'),
      walk: this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'),
      yaw: this.yaw,
      pitch: this.pitch,
      fire: this.locked && this.buttonDown,
      altFire: this.locked && this.button2Down,
      reload: this.keys.has('KeyR'),
      weapon: this.activeKind,
    }
    if (this.pendingShot && this.pendingShot.weapon === this.activeKind) {
      input.shotOrigin = {
        x: this.pendingShot.origin.x,
        y: this.pendingShot.origin.y,
        z: this.pendingShot.origin.z,
      }
      input.shotDir = {
        x: this.pendingShot.dir.x,
        y: this.pendingShot.dir.y,
        z: this.pendingShot.dir.z,
      }
      input.shotMaxDist = this.pendingShot.maxDist
      this.pendingShot = undefined
    }
    this.net.sendInput(input)
  }

  private applyServerState(): void {
    const self = this.net.latestSelf
    if (!self) return
    this.weapon.ammo = self.ammo
    this.weapon.reserve = self.reserve
    this.health = self.health
    this.armor = self.armor

    // The authoritative `alive` flag drives death/respawn (it repeats in every
    // snapshot). The one-shot death/spawn events on the unreliable channel are
    // only an optimization: the death event (handled earlier this frame) gives a
    // precise kill-cam dir; if it was dropped, fall back to the flag here.
    if (!self.alive && !this.dead) {
      this.beginLocalDeathFromAim()
    } else if (self.alive && this.dead) {
      this.respawnLocal()
      return // just teleported; don't reconcile against the stale server pos
    }
    if (this.dead) return // no reconciliation while dead (feet are frozen)

    // Keep the local player responsive. Reconcile only large server disagreement;
    // normal prediction/collision stays local so BSP movement remains stable.
    const dx = self.pos.x - this.feet.x
    const dz = self.pos.z - this.feet.z
    const horizontalErr = Math.hypot(dx, dz)

    // After a (re)spawn, wait until the server actually applies our new position
    // (our inputs/spawn drag it toward us) before reconciling — otherwise the
    // still-stale server pos yanks us across the map and we fall into the void.
    // No time deadline: a deadline could fire while the server still disagrees
    // and tunnel us; converging via proximity is the only safe settle.
    if (!this.posSettled) {
      if (horizontalErr < 96) this.posSettled = true
      else return
    }

    if (this.spawnSent && horizontalErr > 256) {
      this.feet.x = self.pos.x
      this.feet.z = self.pos.z
      this.velocity.x = self.vel.x
      this.velocity.z = self.vel.z
    }
  }

  /** Local death triggered by the snapshot alive flag (the death event was
   * dropped), so synthesize a kill-cam dir from the current aim. */
  private beginLocalDeathFromAim(): void {
    this.updateAimBasis()
    this.spawnSent = false
    this.beginLocalDeath(
      { x: this.feet.x, y: this.feet.y + this.eyeHeight(), z: this.feet.z },
      { x: this.fwd.x, y: this.fwd.y, z: this.fwd.z },
    )
  }

  /** Respawn the local player at a fresh map spawn and tell the server. Driven
   * by the alive flag so it fires exactly once even if spawn events double up. */
  private respawnLocal(): void {
    this.moveToRespawnPoint()
    this.endLocalDeath()
    this.spawnSent = false
    this.sendSpawn()
  }

  private updateRemotePlayers(): void {
    const events = this.net.consumeEvents()
    this.playNetEvents(events)
    this.remotePlayers.update(
      this.net.getInterpolatedPlayers(),
      this.experience.time.elapsed / 1000,
      events,
      this.net.id,
    )
  }

  private playNetEvents(events: NetEvent[]): void {
    for (const event of events) {
      if (event.type === 'shot') {
        if (event.shooterId === this.net.id) continue
        if (event.weapon === 'knife') {
          this.sound.playOneOf(['knifeslash1', 'knifeslash2'], 0.35)
        } else {
          this.sound.playOneOf(['fire1', 'fire2'], 0.35)
          this.spawnRemoteBulletImpact(event.origin, event.dir)
        }
      } else if (event.type === 'reload') {
        if (event.playerId === this.net.id) continue
        this.sound.play('clipout', 0.35)
      } else if (event.type === 'footstep') {
        if (event.playerId === this.net.id) continue
        this.sound.playOneOf(STEP_KEYS, 0.2)
      } else if (event.type === 'hit') {
        this.spawnBlood(event.point, event.dir, event.headshot)
        if (event.targetId === this.net.id) {
          this.health = event.health
        }
        // Show the shooter how much health they took off (their own hits only).
        if (event.shooterId === this.net.id && event.damage > 0) {
          const { width, height } = this.experience.sizes
          this.damageNumbers.spawn(
            new THREE.Vector3(event.point.x, event.point.y, event.point.z),
            event.damage,
            this.experience.camera.instance,
            width,
            height,
          )
        }
      } else if (event.type === 'death') {
        this.spawnBloodDeath(event.point, event.dir, event.headshot)
        // Primary path: gives the kill-cam a precise dir. Respawn is handled by
        // the authoritative alive flag in applyServerState, not a spawn event.
        if (event.playerId === this.net.id && !this.dead) {
          this.spawnSent = false
          this.beginLocalDeath(event.point, event.dir)
        }
      } else if (event.type === 'weapon_drop') {
        if (event.playerId !== this.net.id) {
          this.groundWeapons.addDropped(
            event.weapon,
            new THREE.Vector3(event.pos.x, event.pos.y, event.pos.z),
            event.yaw,
            {
              velocity: new THREE.Vector3(event.vel.x, event.vel.y, event.vel.z),
              delayMs: 250,
              armAfterExit: false,
              ammo: event.ammo,
              reserve: event.reserve,
            },
          )
        }
      }
      // 'spawn' events are advisory; local respawn is driven by the alive flag.
    }
  }

  private spawnBlood(
    point: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
    headshot: boolean,
  ): void {
    this.netShotOrigin.set(point.x, point.y, point.z)
    this.netShotDir.set(dir.x, dir.y, dir.z).normalize()
    this.blood.spawn(this.netShotOrigin, this.netShotDir, headshot)
  }

  /** Heavier blood for a kill: spray, wall spatter, and a growing floor pool. */
  private spawnBloodDeath(
    point: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
    headshot: boolean,
  ): void {
    this.netShotOrigin.set(point.x, point.y, point.z)
    this.netShotDir.set(dir.x, dir.y, dir.z).normalize()
    this.blood.spawnDeath(this.netShotOrigin, this.netShotDir, headshot)
  }

  private spawnRemoteBulletImpact(
    origin: { x: number; y: number; z: number },
    dir: { x: number; y: number; z: number },
  ): void {
    this.netShotOrigin.set(origin.x, origin.y, origin.z)
    this.netShotDir.set(dir.x, dir.y, dir.z).normalize()
    const hit = this.cast(this.netShotOrigin, this.netShotDir, 8192)
    const end = hit
      ? hit.point
      : this.netShotOrigin.clone().addScaledVector(this.netShotDir, 8192)
    // The shot origin is the shooter's eye, so a tracer from there looks like it
    // leaves their head. Offset to the gun muzzle: down toward the weapon, a bit
    // forward and to their right. (Raycast still uses the true origin/dir above.)
    this.netRight.crossVectors(this.netShotDir, UP)
    if (this.netRight.lengthSq() < 1e-6) this.netRight.set(1, 0, 0)
    this.netRight.normalize()
    this.netMuzzle
      .copy(this.netShotOrigin)
      .addScaledVector(this.netShotDir, 22)
      .addScaledVector(this.netRight, 6)
      .addScaledVector(UP, -16)
    // A tracer from the gun reveals where their fire is coming from.
    this.tracers.spawn(
      this.netMuzzle,
      end,
      this.experience.camera.instance.position,
    )
    if (!hit) return
    const n = this.hitNormal(hit, this.netShotDir)
    this.impacts.spawn(hit.point, n)
    this.decals.spawn(hit.point, n)
  }

  private applyFriction(dt: number): void {
    if (!this.onGround) return
    const speed = Math.hypot(this.velocity.x, this.velocity.z)
    if (speed < 0.1) {
      this.velocity.x = 0
      this.velocity.z = 0
      return
    }
    const control = Math.max(speed, M.STOP_SPEED)
    const drop = control * M.FRICTION * dt
    const newSpeed = Math.max(0, speed - drop) / speed
    this.velocity.x *= newSpeed
    this.velocity.z *= newSpeed
  }

  private accelerate(dir: THREE.Vector3, wishSpeed: number, dt: number): void {
    if (wishSpeed <= 0) return
    const current = this.velocity.x * dir.x + this.velocity.z * dir.z
    const add = wishSpeed - current
    if (add <= 0) return
    const accel = this.onGround ? M.ACCELERATE : M.AIR_ACCELERATE
    const capSpeed = this.onGround
      ? wishSpeed
      : Math.min(wishSpeed, M.AIR_WISH_CAP)
    const accelSpeed = Math.min(accel * capSpeed * dt, add)
    this.velocity.x += accelSpeed * dir.x
    this.velocity.z += accelSpeed * dir.z
  }

  private moveHorizontal(dt: number): void {
    const disp = new THREE.Vector3(this.velocity.x, 0, this.velocity.z)
    disp.multiplyScalar(dt)
    const sampleHeights = [M.STEP_HEIGHT + 2, 40, this.currentHeight() - 8]

    for (let iter = 0; iter < 3 && disp.lengthSq() > 1e-8; iter++) {
      const dist = disp.length()
      const dir = disp.clone().multiplyScalar(1 / dist)
      const side = new THREE.Vector3(-dir.z, 0, dir.x)

      let nearest: { hit: THREE.Intersection; allowed: number } | null = null
      for (const h of sampleHeights) {
        for (const lateral of [0, -M.RADIUS, M.RADIUS]) {
          const clearance = lateral === 0 ? M.RADIUS : WALL_SKIN
          const origin = new THREE.Vector3(
            this.feet.x + side.x * lateral,
            this.feet.y + h,
            this.feet.z + side.z * lateral,
          )
          const hit = this.cast(origin, dir, dist + clearance)
          if (!hit) continue
          const allowed = Math.max(0, hit.distance - clearance)
          if (!nearest || allowed < nearest.allowed) nearest = { hit, allowed }
        }
      }

      if (!nearest) {
        this.feet.x += disp.x
        this.feet.z += disp.z
        return
      }

      const allowed = nearest.allowed
      this.feet.x += dir.x * allowed
      this.feet.z += dir.z * allowed

      // Slide along the wall: remove the component heading into the surface.
      const n = nearest.hit.face
        ? nearest.hit.face.normal
            .clone()
            .transformDirection(nearest.hit.object.matrixWorld)
        : null
      if (!n) return
      n.y = 0
      if (n.lengthSq() < 1e-6) return
      n.normalize()
      if (n.dot(dir) > 0) n.negate()

      const remaining = disp.clone().sub(dir.multiplyScalar(allowed))
      remaining.addScaledVector(n, -remaining.dot(n))
      disp.copy(remaining)

      const vInto = this.velocity.x * n.x + this.velocity.z * n.z
      this.velocity.x -= vInto * n.x
      this.velocity.z -= vInto * n.z
    }
  }

  private moveVertical(dt: number): void {
    const vy = this.velocity.y * dt

    if (vy <= 0) {
      const origin = new THREE.Vector3(
        this.feet.x,
        this.feet.y + M.STEP_HEIGHT,
        this.feet.z,
      )
      const far = M.STEP_HEIGHT - vy + 2
      const hit = this.cast(origin, DOWN, far)
      if (hit && this.feet.y + vy <= hit.point.y + 0.5) {
        this.feet.y = hit.point.y
        this.velocity.y = 0
        this.onGround = true
      } else {
        this.feet.y += vy
        this.onGround = false
      }
    } else {
      const origin = new THREE.Vector3(
        this.feet.x,
        this.feet.y + this.currentHeight(),
        this.feet.z,
      )
      const hit = this.cast(origin, UP, vy)
      if (hit) {
        this.velocity.y = 0
      } else {
        this.feet.y += vy
        this.onGround = false
      }
    }
  }

  private resolveHorizontalPenetration(): void {
    const sampleHeights = [M.STEP_HEIGHT + 2, 40, this.currentHeight() - 8]

    for (let iter = 0; iter < 3; iter++) {
      let pushed = false
      for (const h of sampleHeights) {
        const origin = new THREE.Vector3(this.feet.x, this.feet.y + h, this.feet.z)
        for (const dir of WALL_DIRECTIONS) {
          const hit = this.cast(origin, dir, WALL_CLEARANCE)
          if (!hit) continue

          const n = this.hitNormal(hit, dir)
          n.y = 0
          if (n.lengthSq() < 1e-6) continue
          n.normalize()

          const push = WALL_CLEARANCE - hit.distance
          if (push <= 0) continue
          this.feet.addScaledVector(n, push)
          pushed = true
        }
      }
      if (!pushed) return
    }
  }

  private currentHeight(): number {
    return this.duckT >= 1 ? M.DUCK_HEIGHT : M.STAND_HEIGHT
  }

  private handleFiring(): void {
    // Attack only when locked (mouse mode); in drag mode the held button is
    // steering the view instead.
    if (!this.locked) return
    const primary = this.buttonDown
    const secondary = this.button2Down
    if (!primary && !secondary) return

    if (this.weapon.melee) {
      // Knife: left = slash, right = stab (real CS). Both swing the blade.
      const stab = secondary && !primary
      this.updateAimBasis()
      const origin = this.aimOrigin(this.head)
      const hit = this.cast(origin, this.fwd, 48) // ~knife reach
      if (!this.weapon.tryFire(stab, hit !== null)) return
      this.pendingShot = {
        origin: origin.clone(),
        dir: this.fwd.clone(),
        weapon: 'knife',
        maxDist: hit ? hit.distance : 48,
      }
      this.shootAnimDuration = this.weapon.lastFireAnimDuration
      this.shootAnimTimer = this.shootAnimDuration
      this.sound.playOneOf(
        ['knifeslash1', 'knifeslash2'],
        0.5,
        0.96 + Math.random() * 0.08,
      )
      if (hit) window.setTimeout(() => this.sound.play('knifehitwall', 0.5), 90)
      return
    }

    // Gun fires on the left button only (the AK has no secondary fire).
    if (!primary) return
    if (!WEAPON_STATS[this.activeKind].automatic && this.primaryLatched) return
    if (!this.weapon.tryFire()) {
      if (this.weapon.ammo <= 0 && this.weapon.reload()) this.playReload()
      return
    }
    this.primaryLatched = true
    this.shootAnimDuration = 0.12
    this.shootAnimTimer = 0.12 // third-person shoot-recoil window
    this.sound.playOneOf(
      FIRE_KEYS[this.activeKind],
      0.5,
      0.96 + Math.random() * 0.08,
    )

    // CS recoil + accuracy: register the shot, get the spread cone, kick the
    // view (applyLook adds the punch, so the next shots climb).
    const spread = this.fire.fire(
      {
        onGround: this.onGround,
        ducking: this.duckT > 0.5,
        speed: Math.hypot(this.velocity.x, this.velocity.z),
      },
      this.experience.time.elapsed / 1000,
    )

    // Hitscan along the aim, deviated within the spread cone (FireBullets3:
    // each axis is the sum of two uniform[-0.5,0.5] → triangular distribution).
    this.updateAimBasis()
    const x = Math.random() - 0.5 + (Math.random() - 0.5)
    const y = Math.random() - 0.5 + (Math.random() - 0.5)
    this.fwd
      .addScaledVector(this.aimRight, x * spread)
      .addScaledVector(this.aimUp, y * spread)
      .normalize()

    const origin = this.aimOrigin(this.head)
    const hit = this.cast(origin, this.fwd, 8192)
    this.pendingShot = {
      origin: origin.clone(),
      dir: this.fwd.clone(),
      weapon: this.activeKind,
      maxDist: hit ? hit.distance : 8192,
    }
    const end = hit
      ? hit.point
      : origin.clone().addScaledVector(this.fwd, 8192)
    // Start the tracer exactly where the first-person muzzle flash appears on
    // screen: project the overlay muzzle to NDC, then cast a world ray through
    // that screen point (camera FOV + current orientation). Falls back to a
    // simple gun-ish offset if the muzzle isn't available.
    const camW = this.experience.camera.instance
    if (this.weapon.muzzleNdc(this.muzzleNdc)) {
      const t = Math.tan(THREE.MathUtils.degToRad(camW.fov / 2))
      this.tracerRay
        .set(this.muzzleNdc.x * t * camW.aspect, this.muzzleNdc.y * t, -1)
        .applyQuaternion(camW.quaternion)
        .normalize()
      this.tracerStart.copy(origin).addScaledVector(this.tracerRay, 45)
    } else {
      this.tracerStart
        .copy(origin)
        .addScaledVector(this.aimRight, 5)
        .addScaledVector(this.aimUp, -5)
        .addScaledVector(this.fwd, 28)
    }
    this.tracers.spawn(this.tracerStart, end, camW.position)
    if (hit) {
      const n = this.hitNormal(hit, this.fwd)
      this.impacts.spawn(hit.point, n) // brief spark flash
      this.decals.spawn(hit.point, n) // lasting bullet hole
    }
  }

  public resize(): void {
    const { width, height } = this.experience.sizes
    for (const w of Object.values(this.weapons)) w?.resize(width / height)
  }

  public dispose(): void {
    for (const el of this.clickTargets) {
      el?.removeEventListener('click', this.onClick)
    }
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('mousedown', this.onMouseDown)
    window.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('mousemove', this.onMouseMove)
    window.removeEventListener('contextmenu', this.onContextMenu)
    document.removeEventListener('pointerlockchange', this.onPointerLockChange)
    window.removeEventListener('pagehide', this.onPageHide)
    window.removeEventListener('pageshow', this.onPageShow)
    document.removeEventListener('selectstart', this.onSelectStart)
    document.removeEventListener('gesturestart', this.onSelectStart)
    if (document.pointerLockElement) document.exitPointerLock()
    this.experience.renderer.overlay = undefined
    this.remotePlayers.dispose()
    this.blood.dispose()
    this.groundWeapons.dispose()
    this.damageNumbers.dispose()
    this.mobileControls?.dispose()
    this.net.disconnect()
  }
}

function createStarterWeapons(): Partial<Record<WeaponKind, Weapon>> {
  return {
    knife: new Weapon(WEAPON_CONFIGS.knife),
  }
}

function pointInPolygonXZ(
  x: number,
  z: number,
  points: { x: number; z: number }[],
): boolean {
  let inside = false
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]
    const b = points[j]
    const intersects =
      a.z > z !== b.z > z &&
      x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z || Number.EPSILON) + a.x
    if (intersects) inside = !inside
  }
  return inside
}
